import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiFetch, clearStoredAuth, getStoredAuth, setStoredAuth } from "./api";

const AuthContext = createContext(null);

const normalizeUser = (user) => {
  if (!user) return null;
  const investigationOfficerName = user.investigationOfficerName || user.name || "";
  const ioDesignation = user.ioDesignation || user.rank || "";
  return {
    id: user.id,
    username: user.username,
    role: user.role,
    name: user.name || "",
    investigationOfficerName,
    circle: user.circle || "",
    rank: user.rank || "",
    ioDesignation,
    activeStatus: !!user.activeStatus,
    canEdit: !!user.canEdit,
    canDelete: !!user.canDelete,
    isDeleted: !!user.isDeleted,
    timestampUserCreated: user.timestampUserCreated || null,
  };
};

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(() => {
    const stored = getStoredAuth();
    if (!stored?.token || !stored?.user) return null;
    return { token: stored.token, user: normalizeUser(stored.user) };
  });
  const [loading, setLoading] = useState(true);

  const applyAuth = useCallback((nextAuth) => {
    if (!nextAuth) {
      setAuth(null);
      clearStoredAuth();
      return;
    }
    const normalized = {
      token: nextAuth.token,
      user: normalizeUser(nextAuth.user),
    };
    setAuth(normalized);
    setStoredAuth(normalized);
  }, []);

  const login = useCallback(async (username, password) => {
    const res = await apiFetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) {
      const payload = await res.json().catch(() => ({}));
      return { ok: false, message: payload.message || "Login failed" };
    }
    const data = await res.json();
    applyAuth({ token: data.token, user: data.user });
    return { ok: true };
  }, [applyAuth]);

  const logout = useCallback(async () => {
    try {
      await apiFetch("/api/auth/logout", { method: "POST" });
    } catch {
      // ignore network errors
    } finally {
      applyAuth(null);
    }
  }, [applyAuth]);

  const refresh = useCallback(async () => {
    const stored = getStoredAuth();
    if (!stored?.token) {
      setLoading(false);
      return;
    }
    const res = await apiFetch("/api/auth/me");
    if (!res.ok) {
      applyAuth(null);
      setLoading(false);
      return;
    }
    const data = await res.json();
    applyAuth({ token: stored.token, user: data.user });
    setLoading(false);
  }, [applyAuth]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const handler = () => applyAuth(null);
    window.addEventListener("auth:logout", handler);
    return () => window.removeEventListener("auth:logout", handler);
  }, [applyAuth]);

  const value = useMemo(() => {
    const user = auth?.user || null;
    return {
      user,
      token: auth?.token || null,
      isAuthenticated: !!auth?.token,
      canEdit: !!user?.canEdit,
      canDelete: !!user?.canDelete,
      role: user?.role || null,
      loading,
      login,
      logout,
      refresh,
    };
  }, [auth, loading, login, logout, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
};
